import React, { useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Home,
  LayoutDashboard,
  FolderOpen,
  Bot,
  Database,
  GitBranch,

  Settings,
  Key,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MessageCircle,
  GraduationCap,
  BookOpen,
  Archive,
  Layers,
  History,
  Plug,
  Globe,
  UserCheck,
  UsersRound,
  ShieldCheck,
  Sparkles,
  Wrench,
} from 'lucide-react';
import Logo from '../common/Logo';
import AnimatedLogo from '../common/AnimatedLogo';
import { useUIStore, useAuthStore, useProjectStore } from '../../store';
import { useTeamStore } from '../../store/teamStore';
import { ASSISTANT_NAME } from '../../constants/branding';

interface SidebarProps {
  isMobileDrawerOpen?: boolean;
  onMobileClose?: () => void;
}

/**
 * Collapsible sidebar group component.
 * In expanded sidebar: shows header with chevron toggle, hides/shows children.
 * In collapsed sidebar: shows a thin divider, all children visible as icons.
 */
const SidebarGroup: React.FC<{
  id: string;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  showSidebarExpanded: boolean;
  badgeCount?: number;
  onLabelClick?: () => void;
  children: React.ReactNode;
}> = ({ id, label, isExpanded, onToggle, showSidebarExpanded, badgeCount, onLabelClick, children }) => {
  return (
    <div className="pt-2">
      {showSidebarExpanded ? (
        <>
          <div className="flex items-center px-2 pt-2">
            <button
              onClick={onToggle}
              aria-expanded={isExpanded}
              aria-controls={`sidebar-group-${id}`}
              className="flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
              />
              {onLabelClick ? (
                <span
                  onClick={(e) => { e.stopPropagation(); onLabelClick(); }}
                  className="cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 truncate max-w-[180px]"
                  title={label}
                >
                  {label}
                </span>
              ) : (
                label
              )}
            </button>
            {badgeCount !== undefined && badgeCount > 0 && (
              <span
                className="ml-auto flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full"
                aria-label={`${badgeCount} pending`}
              >
                {badgeCount > 99 ? '99+' : badgeCount}
              </span>
            )}
          </div>
          {isExpanded && (
            <div id={`sidebar-group-${id}`} className="space-y-1 pt-1">
              {children}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Collapsed sidebar: thin divider, show all items as icons */}
          <div className="border-t border-gray-200 dark:border-gray-700 my-1 mx-2" />
          <div className="space-y-1">
            {children}
          </div>
        </>
      )}
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ isMobileDrawerOpen, onMobileClose }) => {
  // AC-1708: harden against a partial/undefined UI store — `sidebarGroups`
  // defaults to {} so the group-expansion reads below can't crash if the slice
  // is momentarily undefined (e.g. before hydration, or under a partial test
  // mock). The real store always provides it, so production behavior is
  // unchanged: an empty map yields the same defaults (project expanded, others
  // collapsed) via the `!== false` / `=== true` checks.
  const {
    sidebarOpen,
    setSidebarOpen,
    sidebarGroups = {},
    toggleSidebarGroup,
    toggleAssistant,
  } = useUIStore();
  const { logout, user } = useAuthStore();
  const { currentProject, setCurrentOperation } = useProjectStore();
  const { pendingInvitations, loadMyInvitations } = useTeamStore();
  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    onMobileClose?.();
    logout();
    navigate('/login');
  };

  const handleOperationNameClick = () => {
    if (currentProject) {
      setCurrentOperation(currentProject);
      navigate(`/projects/${currentProject.project_id}`);
      onMobileClose?.();
    }
  };

  const handleNavClick = () => {
    window.dispatchEvent(new CustomEvent('agent-salute'));
    onMobileClose?.();
  };

  // Load pending invitations for sidebar badge
  useEffect(() => {
    loadMyInvitations();
  }, [loadMyInvitations]);

  // Focus trap: when drawer opens on mobile, focus first nav item
  useEffect(() => {
    if (isMobileDrawerOpen && sidebarRef.current) {
      const firstLink = sidebarRef.current.querySelector('a[href]') as HTMLElement | null;
      if (firstLink) {
        firstLink.focus();
      }
    }
  }, [isMobileDrawerOpen]);

  // Focus trap: trap Tab/Shift-Tab within drawer when open
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isMobileDrawerOpen) return;
    if (e.key !== 'Tab') return;

    const focusableElements = sidebarRef.current?.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }, [isMobileDrawerOpen]);

  // Shared NavLink class builder
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
      isActive
        ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/30 dark:text-primary-400'
        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 hover:text-gray-900 dark:hover:text-warm-white'
    }`;

  // === Navigation item definitions ===

  const navigationItems = [
    { name: 'Command Center', href: '/dashboard', icon: Home, dataTutorial: undefined },
    { name: "What's New", href: '/whats-new', icon: Sparkles, dataTutorial: undefined, tooltip: 'Latest features and improvements' },
    { name: 'Projects', href: '/projects', icon: FolderOpen, dataTutorial: 'nav-projects' },
  ];

  // Project: primary items (daily use)
  const primaryProjectItems = currentProject ? [
    { name: 'Overview', href: `/projects/${currentProject.project_id}`, icon: LayoutDashboard, dataTutorial: undefined, end: true },
    { name: 'Workflows', href: `/projects/${currentProject.project_id}/workflows`, icon: GitBranch, dataTutorial: 'nav-workflows' },
    { name: 'Agents', href: `/projects/${currentProject.project_id}/agents`, icon: Bot, dataTutorial: 'nav-agents' },
    { name: 'History', href: `/projects/${currentProject.project_id}/history`, icon: History, dataTutorial: undefined },
  ] : [];

  // Project: configuration items (setup, less frequent)
  const configProjectItems = currentProject ? [
    { name: 'Schemas', href: `/projects/${currentProject.project_id}/schemas`, icon: Database, dataTutorial: undefined },
    { name: 'Registries', href: `/projects/${currentProject.project_id}/registries`, icon: Archive, dataTutorial: undefined },
    { name: 'Queues', href: `/projects/${currentProject.project_id}/queues`, icon: Layers, dataTutorial: undefined },
    { name: 'Integrations', href: `/projects/${currentProject.project_id}/integrations`, icon: Plug, dataTutorial: 'nav-integrations' },
  ] : [];

  const communityItems = [
    { name: 'Explore', href: '/explore', icon: Globe, dataTutorial: undefined, tooltip: 'Browse public projects from the community' },
    { name: 'Following', href: '/following', icon: UserCheck, dataTutorial: undefined, tooltip: 'Projects from users you follow' },
    { name: 'Teams', href: '/teams', icon: UsersRound, dataTutorial: undefined, badgeCount: pendingInvitations.length, tooltip: 'Your teams and shared projects' },
  ];

  const learnItems = [
    { name: 'Bootcamp', href: '/bootcamp', icon: GraduationCap, dataTutorial: undefined, tooltip: 'Tutorials and getting started guides' },
    { name: 'Knowledge Base', href: '/knowledge-base', icon: BookOpen, dataTutorial: undefined, tooltip: 'Upload and manage documents for RAG' },
  ];

  // Determine if we should show expanded (with text labels) view
  const showExpanded = isMobileDrawerOpen || sidebarOpen;

  /**
   * Render a standard nav item (NavLink)
   */
  const renderNavItem = (item: { name: string; href: string; icon: React.ElementType; dataTutorial?: string; end?: boolean; tooltip?: string; badgeCount?: number }) => (
    <NavLink
      key={item.name}
      to={item.href}
      end={'end' in item ? item.end : false}
      data-tutorial={item.dataTutorial}
      onClick={handleNavClick}
      title={!showExpanded ? item.name : (item.tooltip || undefined)}
      className={navLinkClass}
    >
      <div className="relative flex-shrink-0">
        <item.icon
          className={`${showExpanded ? 'mr-3' : 'mx-auto'} w-5 h-5`}
          aria-hidden="true"
        />
        {/* Badge in collapsed mode: dot on icon */}
        {'badgeCount' in item && typeof item.badgeCount === 'number' && item.badgeCount > 0 && !showExpanded && (
          <span
            className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full"
            aria-label={`${item.badgeCount} pending`}
          >
            {item.badgeCount > 9 ? '9+' : item.badgeCount}
          </span>
        )}
      </div>
      {showExpanded && (
        <span className="flex items-center gap-2">
          {item.name}
          {'badgeCount' in item && typeof item.badgeCount === 'number' && item.badgeCount > 0 && (
            <span
              className="flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full"
              aria-label={`${item.badgeCount} pending invitations`}
            >
              {item.badgeCount > 99 ? '99+' : item.badgeCount}
            </span>
          )}
        </span>
      )}
    </NavLink>
  );

  return (
    <div
      ref={sidebarRef}
      role="navigation"
      onKeyDown={handleKeyDown}
      className={`fixed inset-y-0 left-0 z-50 bg-white dark:bg-cap-dark border-r border-gray-200 dark:border-gray-700 transition-transform duration-300 ${
        isMobileDrawerOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64'
      } md:translate-x-0 md:transition-all ${
        sidebarOpen ? 'md:w-64' : 'md:w-16'
      }`}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-4">
          {showExpanded ? (
            <div className="flex flex-col items-center">
              <div className="hidden md:flex justify-end w-full mb-1">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </div>
              <AnimatedLogo size={120} />
              <span className="text-lg font-bold text-gray-900 dark:text-warm-white -mt-1">Agent Commander</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Logo variant="attention-light" size={36} themeAware />
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-0 overflow-y-auto scrollbar-thin">
          {/* Core navigation - always visible */}
          <div className="space-y-1">
            {navigationItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end
                data-tutorial={item.dataTutorial}
                onClick={handleNavClick}
                title={!showExpanded ? item.name : undefined}
                className={navLinkClass}
              >
                <item.icon
                  className={`${showExpanded ? 'mr-3' : 'mx-auto'} w-5 h-5 flex-shrink-0`}
                  aria-hidden="true"
                />
                {showExpanded && item.name}
              </NavLink>
            ))}
          </div>

          {/* Project section - collapsible, expanded by default */}
          {currentProject && (
            <SidebarGroup
              id="project"
              label={currentProject.name}
              isExpanded={sidebarGroups.project !== false}
              onToggle={() => toggleSidebarGroup('project')}
              showSidebarExpanded={showExpanded}
              onLabelClick={handleOperationNameClick}
            >
              {primaryProjectItems.map(renderNavItem)}

              {/* Configuration sub-group */}
              {showExpanded ? (
                <div className="pt-1">
                  <button
                    onClick={() => toggleSidebarGroup('config')}
                    aria-expanded={sidebarGroups.config === true}
                    aria-controls="sidebar-group-config"
                    className="flex items-center gap-1 px-2 py-1.5 w-full text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors rounded-md"
                  >
                    <ChevronDown
                      className={`w-3 h-3 transition-transform duration-200 ${sidebarGroups.config ? '' : '-rotate-90'}`}
                    />
                    <Wrench className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                    Configuration
                  </button>
                  {sidebarGroups.config && (
                    <div id="sidebar-group-config" className="space-y-1 pl-2">
                      {configProjectItems.map(renderNavItem)}
                    </div>
                  )}
                </div>
              ) : (
                // Collapsed sidebar: show config items as icons with subtle separator
                <>
                  <div className="border-t border-gray-100 dark:border-gray-800 my-0.5 mx-3" />
                  {configProjectItems.map(renderNavItem)}
                </>
              )}
            </SidebarGroup>
          )}

          {/* Community section - collapsible, collapsed by default */}
          <SidebarGroup
            id="community"
            label="Community"
            isExpanded={sidebarGroups.community === true}
            onToggle={() => toggleSidebarGroup('community')}
            showSidebarExpanded={showExpanded}
            badgeCount={pendingInvitations.length}
          >
            {communityItems.map(renderNavItem)}
          </SidebarGroup>

          {/* Learn section - collapsible, collapsed by default */}
          <SidebarGroup
            id="learn"
            label="Learn"
            isExpanded={sidebarGroups.learn === true}
            onToggle={() => toggleSidebarGroup('learn')}
            showSidebarExpanded={showExpanded}
          >
            {learnItems.map(renderNavItem)}
          </SidebarGroup>
        </nav>

        {/* Bottom navigation */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-2 space-y-1">
          {/* Agent Commander launcher — opens the global copilot panel.
              AC-1699: drives the shared useUIStore flag so the same panel can
              also be opened from in-page launchers. */}
          <button
            onClick={() => {
              toggleAssistant();
              onMobileClose?.();
            }}
            data-tutorial="nav-assistant"
            className="group flex items-center w-full px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 hover:text-gray-900 dark:hover:text-warm-white transition-colors"
            title={`Ask ${ASSISTANT_NAME}`}
          >
            <MessageCircle
              className={`${showExpanded ? 'mr-3' : 'mx-auto'} w-5 h-5 flex-shrink-0`}
              aria-hidden="true"
            />
            {showExpanded && `Ask ${ASSISTANT_NAME}`}
          </button>

          {/* Admin link - only visible for admin users */}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              onClick={handleNavClick}
              title={!showExpanded ? 'Admin' : undefined}
              className={navLinkClass}
            >
              <ShieldCheck
                className={`${showExpanded ? 'mr-3' : 'mx-auto'} w-5 h-5 flex-shrink-0`}
                aria-hidden="true"
              />
              {showExpanded && 'Admin'}
            </NavLink>
          )}

          <NavLink
            to="/settings"
            onClick={handleNavClick}
            title={!showExpanded ? 'Settings' : undefined}
            className={navLinkClass}
            data-tutorial="nav-settings"
          >
            <Settings
              className={`${showExpanded ? 'mr-3' : 'mx-auto'} w-5 h-5 flex-shrink-0`}
              aria-hidden="true"
            />
            {showExpanded && 'Settings'}
          </NavLink>

          {/* AC-1514: BYOK API keys page. Sibling of /settings so it surfaces
              as a top-level navigation item rather than being hidden inside a
              tab (BYOK is a frequent action during onboarding). */}
          <NavLink
            to="/settings/api-keys"
            onClick={handleNavClick}
            title={!showExpanded ? 'API Keys' : undefined}
            className={navLinkClass}
            data-tutorial="nav-api-keys"
          >
            <Key
              className={`${showExpanded ? 'mr-3' : 'mx-auto'} w-5 h-5 flex-shrink-0`}
              aria-hidden="true"
            />
            {showExpanded && 'API Keys'}
          </NavLink>

          {/* Separator before Logout */}
          <div className="border-t border-gray-200 dark:border-gray-700 !mt-1 !pt-1" />

          <button
            onClick={handleLogout}
            title={!showExpanded ? 'Logout' : undefined}
            className="group flex items-center w-full px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 hover:text-gray-900 dark:hover:text-warm-white transition-colors"
          >
            <LogOut
              className={`${showExpanded ? 'mr-3' : 'mx-auto'} w-5 h-5 flex-shrink-0`}
              aria-hidden="true"
            />
            {showExpanded && 'Logout'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
